import moment from "moment";

import { api } from "services";
import { Quote } from "types/quotes";

const PAGE = 200;
const CAP = 3000;
// /Quote/search é lenta pra paginar em série — mesma pegadinha documentada
// pro Dashboard (docttorpay-api-quirks): buscar em paralelo, concorrência ≤4.
const CONCURRENCY = 4;

// "Concluído" pro relatório = status 5 (Em Agendamento, cartão já aprovado,
// repasse ainda não caiu) + 6 (Concluído, repasse já efetuado) — mesma regra
// de "transacionado" usada no Dashboard. Ver memória docttorpay-status-semantics.
const CONCLUDED_STATUSES = [5, 6];

async function fetchPage(
  status: number,
  page: number,
  params: Record<string, unknown>,
  signal?: AbortSignal
) {
  return api.get("/Quote/search", {
    params: { ...params, status, page, size: PAGE },
    signal,
  });
}

async function fetchByStatus(
  status: number,
  params: Record<string, unknown>,
  signal?: AbortSignal
): Promise<Quote[]> {
  const first = await fetchPage(status, 1, params, signal);
  const firstBatch: Quote[] = Array.isArray(first.data) ? first.data : [];
  const total = Number(first.headers["x-total-count"]) || firstBatch.length;
  const totalPages = Math.min(
    Math.ceil(total / PAGE) || 1,
    Math.ceil(CAP / PAGE)
  );

  const rows = firstBatch;
  const remainingPages = Array.from(
    { length: Math.max(totalPages - 1, 0) },
    (_, i) => i + 2
  );

  for (let i = 0; i < remainingPages.length; i += CONCURRENCY) {
    const chunk = remainingPages.slice(i, i + CONCURRENCY);
    const responses = await Promise.all(
      chunk.map((page) => fetchPage(status, page, params, signal))
    );
    for (const res of responses) {
      rows.push(...(Array.isArray(res.data) ? res.data : []));
    }
  }

  return rows.slice(0, CAP);
}

export async function fetchConcludedQuotes(
  params: Record<string, unknown>,
  signal?: AbortSignal
): Promise<Quote[]> {
  const batches = await Promise.all(
    CONCLUDED_STATUSES.map((status) => fetchByStatus(status, params, signal))
  );
  return batches.flat();
}

// Período padrão do relatório ao abrir: últimos 15 dias, pra não puxar o
// histórico inteiro do cliente de cara.
export function defaultReportPeriod() {
  return {
    beginDate: moment().subtract(14, "days").startOf("day"),
    endDate: moment().endOf("day"),
  };
}

// A API não filtra por data de transação, só por data de CRIAÇÃO
// (beginDateCreate/endDateCreate). Sem isso, toda busca varre o histórico
// inteiro do profissional/empresa mesmo pra ver só os últimos 15 dias — é
// o que deixa o relatório lento. Aceita-se o risco de perder um orçamento
// criado há muito tempo e concluído só agora (fora da folga de 90 dias).
const CREATION_BUFFER_DAYS = 90;

export function creationDateBounds(beginDate?: unknown, endDate?: unknown) {
  return {
    beginDateCreate: beginDate
      ? moment(beginDate as any)
          .subtract(CREATION_BUFFER_DAYS, "days")
          .format("YYYY-MM-DD")
      : undefined,
    endDateCreate: endDate
      ? moment(endDate as any).format("YYYY-MM-DD")
      : undefined,
  };
}

const isZeroDate = (d?: string | null) => !d || d.startsWith("0001-01-01");

// /Quote/search não tem filtro de data de transação — filtra e ordena no
// cliente por payment.paymentDate (data em que entrou em Agendamento), a
// única data confiável presente em ambos status 5 e 6.
export function filterAndSortConcluded(
  quotes: Quote[],
  beginDate?: unknown,
  endDate?: unknown
): Quote[] {
  const start = beginDate ? moment(beginDate as any).startOf("day") : null;
  const end = endDate ? moment(endDate as any).endOf("day") : null;

  return quotes
    .filter((q) => {
      const pd = q.payment?.paymentDate;
      if (isZeroDate(pd)) return !start && !end;
      const date = moment(pd);
      if (start && date.isBefore(start)) return false;
      if (end && date.isAfter(end)) return false;
      return true;
    })
    .sort((a, b) => {
      const da = isZeroDate(a.payment?.paymentDate)
        ? 0
        : moment(a.payment.paymentDate).valueOf();
      const db = isZeroDate(b.payment?.paymentDate)
        ? 0
        : moment(b.payment.paymentDate).valueOf();
      return db - da;
    });
}
